import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HeartButton } from "@/components/ui/heart-button";
import { Trash2, Edit, MoreVertical, Reply } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
interface PinCommentsProps {
  comments: any[];
  pinId: string;
  user: any;
  onCommentAdded: () => void;
  refreshComments: () => void;
  onReplyClick?: (commentId: string, username: string) => void;
}
export function PinComments({
  comments,
  pinId,
  user,
  onCommentAdded,
  refreshComments,
  onReplyClick
}: PinCommentsProps) {
  const [commentText, setCommentText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [replyingTo, setReplyingTo] = useState<{
    id: string;
    username: string;
  } | null>(null);
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !user) return;
    setIsSubmitting(true);
    try {
      // Get the current user session to ensure user is authenticated
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error("Você precisa estar logado para comentar");
        return;
      }
      const {
        error
      } = await supabase.from("pin_comments").insert({
        pin_id: pinId,
        user_id: user.id,
        content: commentText,
        parent_id: replyingTo?.id || null
      });
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      setCommentText("");
      setReplyingTo(null);
      onCommentAdded();
      toast.success("Comentário adicionado");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Erro ao adicionar comentário");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;
    try {
      const {
        error
      } = await supabase.from("pin_comments").delete().eq("id", commentId).eq("user_id", user.id);
      if (error) throw error;
      refreshComments();
      toast.success("Comentário excluído");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Erro ao excluir comentário");
    }
  };
  const handleEditComment = async (commentId: string) => {
    if (!user || !editText.trim()) return;
    try {
      const {
        error
      } = await supabase.from("pin_comments").update({
        content: editText
      }).eq("id", commentId).eq("user_id", user.id);
      if (error) throw error;
      setEditingComment(null);
      refreshComments();
      toast.success("Comentário atualizado");
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error("Erro ao atualizar comentário");
    }
  };
  const startEditing = (comment: any) => {
    setEditingComment(comment.id);
    setEditText(comment.content);
  };
  const startReplying = (comment: any) => {
    if (onReplyClick) {
      onReplyClick(comment.id, comment.profile?.username || "Usuário");
    } else {
      setReplyingTo({
        id: comment.id,
        username: comment.profile?.username || "Usuário"
      });
      setCommentText("");
    }
  };
  const cancelReply = () => {
    setReplyingTo(null);
  };
  const handleToggleLike = async (commentId: string, isLiked: boolean) => {
    if (!user) {
      toast.error("Você precisa estar logado para curtir comentários");
      return;
    }
    try {
      // Check current session
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error("Sessão expirada. Faça login novamente.");
        return;
      }

      // Use the local database operation instead of the edge function
      if (isLiked) {
        // Unlike
        const {
          error
        } = await supabase.from("pin_comment_likes").delete().eq("comment_id", commentId).eq("user_id", user.id);
        if (error) throw error;
      } else {
        // Like
        const {
          error
        } = await supabase.from("pin_comment_likes").insert({
          comment_id: commentId,
          user_id: user.id
        });
        if (error) throw error;
      }

      // Refresh comments to get updated like count
      refreshComments();
    } catch (error) {
      console.error("Error toggling comment like:", error);
      toast.error("Erro ao curtir comentário");
    }
  };

  // Group comments by parent_id
  const parentComments = comments.filter(comment => !comment.parent_id);
  const childCommentsMap = comments.reduce((acc: Record<string, any[]>, comment) => {
    if (comment.parent_id) {
      if (!acc[comment.parent_id]) {
        acc[comment.parent_id] = [];
      }
      acc[comment.parent_id].push(comment);
    }
    return acc;
  }, {});
  return <div className="space-y-6">
      <h3 className="text-lg font-medium">Comentários ({comments.length})</h3>

      {user && !onReplyClick && <form onSubmit={handleSubmitComment} className="flex gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.avatar_url} />
            <AvatarFallback>
              {user.username?.charAt(0)?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 flex flex-col gap-2">
            {replyingTo && <div className="bg-muted p-2 rounded-md flex items-center justify-between">
                <span className="text-sm">
                  Respondendo para <span className="font-medium">{replyingTo.username}</span>
                </span>
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={cancelReply}>
                  Cancelar
                </Button>
              </div>}
            <div className="flex gap-2">
              <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={replyingTo ? "Escreva sua resposta..." : "Adicione um comentário..."} className="flex-1" disabled={isSubmitting} />
              <Button type="submit" disabled={!commentText.trim() || isSubmitting}>
                Enviar
              </Button>
            </div>
          </div>
        </form>}

      {!user && !onReplyClick}

      <div className="space-y-4">
        {parentComments.map(comment => <div key={comment.id} className="space-y-3">
            <div className="flex gap-3 p-3 rounded-lg hover:bg-muted/20">
              <Link to={`/profile/${comment.user_id}`}>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={comment.profile?.avatar_url} />
                  <AvatarFallback>
                    {comment.profile?.username?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-left">
                    <Link to={`/profile/${comment.user_id}`} className="hover:underline">
                      {comment.profile?.username || "Usuário"}{" "}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {user && comment.user_id === user.id && <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => startEditing(comment)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteComment(comment.id)} className="text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>}
                </div>

                {editingComment === comment.id ? <div className="mt-1 flex gap-2">
                    <Input value={editText} onChange={e => setEditText(e.target.value)} className="flex-1" />
                    <Button size="sm" onClick={() => handleEditComment(comment.id)} disabled={!editText.trim()}>
                      Salvar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingComment(null)}>
                      Cancelar
                    </Button>
                  </div> : <p className="mt-1 text-left w-full">{comment.content}</p>}

                <div className="mt-2 flex items-center gap-4">
                  <HeartButton initialCount={comment.likes_count} isLiked={comment.user_has_liked} onToggle={async () => {
                await handleToggleLike(comment.id, comment.user_has_liked);
              }} />
                  
                  {user && <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 text-sm" onClick={() => startReplying(comment)}>
                      <Reply className="h-3.5 w-3.5" />
                      Responder
                    </Button>}
                </div>
              </div>
            </div>
            
            {/* Replies */}
            {childCommentsMap[comment.id] && childCommentsMap[comment.id].length > 0 && <div className="ml-10 space-y-3">
                {childCommentsMap[comment.id].map((reply: any) => <div key={reply.id} className="flex gap-3 p-3 rounded-lg hover:bg-muted/20 border-l-2 border-muted">
                    <Link to={`/profile/${reply.user_id}`}>
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={reply.profile?.avatar_url} />
                        <AvatarFallback>
                          {reply.profile?.username?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-left">
                          <Link to={`/profile/${reply.user_id}`} className="hover:underline">
                            {reply.profile?.username || "Usuário"}{" "}
                          </Link>
                          <span className="text-xs text-muted-foreground">
                            {new Date(reply.created_at).toLocaleDateString()}
                          </span>
                        </div>

                        {user && reply.user_id === user.id && <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full">
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => startEditing(reply)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteComment(reply.id)} className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>}
                      </div>

                      {editingComment === reply.id ? <div className="mt-1 flex gap-2">
                          <Input value={editText} onChange={e => setEditText(e.target.value)} className="flex-1" />
                          <Button size="sm" onClick={() => handleEditComment(reply.id)} disabled={!editText.trim()}>
                            Salvar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingComment(null)}>
                            Cancelar
                          </Button>
                        </div> : <p className="mt-1 text-left w-full">{reply.content}</p>}

                      <div className="mt-2 flex items-center gap-4">
                        <HeartButton initialCount={reply.likes_count} isLiked={reply.user_has_liked} onToggle={async () => {
                  await handleToggleLike(reply.id, reply.user_has_liked);
                }} />
                        
                        {user && <Button variant="ghost" size="sm" className="h-6 px-2 gap-1 text-xs" onClick={() => startReplying(reply)}>
                            <Reply className="h-3 w-3" />
                            Responder
                          </Button>}
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>)}
      </div>
    </div>;
}